import React, { useState, useMemo } from 'react';
import { Table } from './Table';
import { ToolbarAction } from './TableToolbar';
import { DefaultTableModel } from './TableModel';
import { ColumnDef, TableConfig } from './types';
import { StringDataType, NumberDataType, validResult, invalidResult } from './DataType';
import { ChangeSet, SaveResult } from './ChangeTracker';
import { PageHeader } from '../page-header/PageHeader';

/**
 * Comprehensive example demonstrating change tracking with batch saves:
 * - Make multiple edits (updates)
 * - Insert new rows
 * - Delete rows
 * - Review all changes in a summary dialog
 * - Save all changes in a single batch
 * - Simulated backend API integration
 */

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  stock: number;
}

// Sample data
const initialData: Product[] = [
  { id: 1, name: 'Laptop', category: 'Electronics', price: 999.99, stock: 15 },
  { id: 2, name: 'Mouse', category: 'Electronics', price: 29.99, stock: 150 },
  { id: 3, name: 'Keyboard', category: 'Electronics', price: 79.99, stock: 80 },
  { id: 4, name: 'Monitor', category: 'Electronics', price: 299.99, stock: 45 },
  { id: 5, name: 'Desk Chair', category: 'Furniture', price: 199.99, stock: 30 },
];

export function ChangeTrackingExample() {
  const [updateCounter, setUpdateCounter] = useState(0);
  const [activityLog, setActivityLog] = useState<string[]>([]);

  const logActivity = (message: string) => {
    setActivityLog(prev => [`[${new Date().toLocaleTimeString()}] ${message}`, ...prev].slice(0, 20));
  };

  // Save changes handler - simulates backend API
  const handleSaveChanges = async (changes: ChangeSet<Product>): Promise<SaveResult> => {
    logActivity(`Saving ${changes.inserts.length} inserts, ${changes.updates.size} updates, ${changes.deletes.length} deletes...`);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Simulate occasional errors (10% chance)
    if (Math.random() < 0.1) {
      logActivity('❌ Save failed - server error');
      return {
        success: false,
        summary: {
          inserted: 0,
          updated: 0,
          deleted: 0,
          failed: changes.inserts.length + changes.updates.size + changes.deletes.length,
        },
        errors: new Map([['server', 'Database connection failed']]),
      };
    }

    // Process inserts
    const insertedIds = new Map<string, number>();
    changes.inserts.forEach(insert => {
      const newId = Math.max(...initialData.map(p => p.id)) + insertedIds.size + 1;
      insertedIds.set(insert.tempId, newId);
      logActivity(`✅ Inserted product: ${insert.row.name} (ID: ${newId})`);
    });

    // Process updates
    changes.updates.forEach((cellChanges, rowId) => {
      const product = initialData.find(p => p.id === rowId);
      cellChanges.forEach(change => {
        logActivity(`✅ Updated ${product?.name ?? rowId}: ${change.columnId} = ${change.newValue}`);
      });
    });

    // Process deletes
    changes.deletes.forEach(del => {
      logActivity(`✅ Deleted product: ${del.row.name} (ID: ${del.rowId})`);
    });

    return {
      success: true,
      insertedIds,
      summary: {
        inserted: changes.inserts.length,
        updated: changes.updates.size,
        deleted: changes.deletes.length,
        failed: 0,
      },
    };
  };

  // Define columns
  const columns: ColumnDef<Product>[] = [
    {
      id: 'id',
      header: 'ID',
      width: 80,
      accessor: (row) => row.id,
      dataType: new NumberDataType({ integer: true }),
      sortable: true,
      resizable: true,
      editable: false, // ID is not editable
    },
    {
      id: 'name',
      header: 'Product Name',
      width: 200,
      accessor: (row) => row.name,
      dataType: new StringDataType({
        required: true,
        minLength: 3,
        maxLength: 100,
      }),
      sortable: true,
      resizable: true,
      editable: true,
    },
    {
      id: 'category',
      header: 'Category',
      width: 150,
      accessor: (row) => row.category,
      dataType: new StringDataType({ required: true }),
      sortable: true,
      resizable: true,
      editable: true,
    },
    {
      id: 'price',
      header: 'Price ($)',
      width: 120,
      accessor: (row) => row.price,
      dataType: new NumberDataType({
        required: true,
        min: 0.01,
        max: 100000,
        decimals: 2,
      }),
      sortable: true,
      resizable: true,
      editable: true,
    },
    {
      id: 'stock',
      header: 'Stock',
      width: 100,
      accessor: (row) => row.stock,
      dataType: new NumberDataType({
        required: true,
        integer: true,
        min: 0,
        max: 10000,
      }),
      sortable: true,
      resizable: true,
      editable: true,
    },
  ];

  // Create model with change tracking - use useMemo to persist across renders
  const model = useMemo(() => {
    const m = new DefaultTableModel(columns, [...initialData], {
      enableChangeTracking: true,
      getRowId: (row) => row.id,
      saveChanges: handleSaveChanges,
    });

    // Row-level validator
    m.rowValidator = (row) => {
      const errors: string[] = [];

      // Business rule: Electronics must be priced >= $10
      if (row.category === 'Electronics' && row.price < 10) {
        errors.push('Electronics must be priced at least $10');
      }

      return errors.length > 0 ? invalidResult(errors) : validResult();
    };

    return m;
  }, []); // Empty deps - only create once

  // Helper to add a new row
  const handleAddProduct = () => {
    const newProduct: Product = {
      id: Date.now(), // Temporary ID
      name: 'New Product',
      category: 'Electronics',
      price: 99.99,
      stock: 10,
    };
    model.insertRow(newProduct);
    logActivity(`➕ Added new product (double-click cells to edit)`);
    setUpdateCounter(c => c + 1);
  };

  // Helper to delete selected row
  const handleDeleteSelected = () => {
    if (model.selectedRowIndex !== undefined) {
      const row = model.getRow(model.selectedRowIndex);
      if (row) {
        model.deleteRow(model.selectedRowIndex);
        logActivity(`🗑️ Marked ${row.name} for deletion`);
        setUpdateCounter(c => c + 1);
      }
    }
  };

  // Table configuration
  const config: TableConfig = {
    enableEditing: true,
    enableChangeTracking: true,
    showSaveButtons: true,
    showValidationErrors: true,
    enableSearch: true,
    enableColumnResizing: true,
    enableColumnChooser: true,
    showToolbar: true,
    rowHeight: 36,
    headerHeight: 40,
    striped: true,
    hoverable: true,
    showColumnBorders: true,
    showRowBorders: true,
  };

  const changeCounts = model.changeTracker?.getChangeCounts();

  const toolbarActions: ToolbarAction[] = [
    {
      label: 'Add Product',
      onClick: handleAddProduct,
      variant: 'primary',
      icon: '➕',
    },
    {
      label: 'Delete Selected',
      onClick: handleDeleteSelected,
      disabled: model.selectedRowIndex === undefined,
      variant: 'danger',
      icon: '🗑️',
    },
    {
      label: 'Refresh',
      onClick: () => setUpdateCounter(c => c + 1),
      variant: 'secondary',
      icon: '🔄',
    },
  ];

  return (
    <div style={{ backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
      <PageHeader title="Change Tracking & Batch Save Example" />
      <div style={{ padding: '20px' }}>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '4px' }}>
        <h3 style={{ color: '#e0e0e0', margin: '0 0 10px 0', fontSize: '16px' }}>How to Use:</h3>
        <ul style={{ color: '#b0b0b0', marginLeft: '20px', fontSize: '14px', lineHeight: '1.6' }}>
          <li><strong>Edit cells:</strong> Double-click any cell (except ID) and make changes</li>
          <li><strong>Add rows:</strong> Click "Add Product" in the toolbar</li>
          <li><strong>Delete rows:</strong> Click a row to select it, then click "Delete Selected" in toolbar</li>
          <li><strong>Review changes:</strong> See the orange badge in toolbar showing pending changes</li>
          <li><strong>Save:</strong> Click "Save Changes" to review and commit all changes at once</li>
          <li><strong>Discard:</strong> Click "Discard" to cancel all pending changes</li>
        </ul>
      </div>

      {changeCounts && changeCounts.total > 0 && (
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#2d3b00', border: '1px solid #5a7300', borderRadius: '4px' }}>
          <div style={{ color: '#d4e157', fontSize: '14px', fontWeight: 600 }}>
            ⚠️ You have {changeCounts.total} unsaved change{changeCounts.total !== 1 ? 's' : ''}:
          </div>
          <ul style={{ color: '#c5d86d', marginLeft: '20px', fontSize: '13px', marginTop: '8px', marginBottom: '0' }}>
            {changeCounts.inserts > 0 && <li>{changeCounts.inserts} row(s) inserted</li>}
            {changeCounts.updates > 0 && <li>{changeCounts.updates} row(s) updated</li>}
            {changeCounts.deletes > 0 && <li>{changeCounts.deletes} row(s) deleted</li>}
          </ul>
        </div>
      )}

      <Table
        model={model}
        config={config}
        height={400}
        width={1200}
        onRowClick={(row, index) => {
          model.setSelectedRow(index);
          setUpdateCounter(c => c + 1);
        }}
        toolbarActions={toolbarActions}
        externalUpdateTrigger={updateCounter}
      />

      {activityLog.length > 0 && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ color: '#e0e0e0', margin: 0, fontSize: '16px' }}>Activity Log:</h3>
            <button
              onClick={() => setActivityLog([])}
              style={{
                padding: '4px 12px',
                backgroundColor: '#3a3a3a',
                color: '#e0e0e0',
                border: '1px solid #555',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Clear
            </button>
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {activityLog.map((log, i) => (
              <div
                key={i}
                style={{
                  color: log.includes('✅') ? '#4caf50' : log.includes('❌') ? '#ff4444' : log.includes('➕') ? '#2196f3' : log.includes('🗑️') ? '#ff9800' : '#e0e0e0',
                  marginBottom: '4px',
                }}
              >
                {log}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
